/**
 * Small local-date helpers for the medication/client/caregiver features.
 * Local dates are ISO yyyy-mm-dd in the device timezone, matching how
 * occurrences expose localDate/localTime.
 */

export function toLocalISODate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function todayLocalISODate(): string {
  return toLocalISODate(new Date())
}

export function addDaysISO(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map((part) => Number(part))
  const date = new Date(y, (m ?? 1) - 1, d ?? 1)
  date.setDate(date.getDate() + days)
  return toLocalISODate(date)
}

/** Monday-first week (ISO) containing the given local date. */
export function weekStartISO(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((part) => Number(part))
  const date = new Date(y, (m ?? 1) - 1, d ?? 1)
  const weekday = (date.getDay() + 6) % 7 // 0 = Monday
  date.setDate(date.getDate() - weekday)
  return toLocalISODate(date)
}

export function weekDaysISO(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, index) => addDaysISO(weekStart, index))
}

const DUTCH_WEEKDAYS_SHORT = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'] as const
const DUTCH_WEEKDAYS_FULL = [
  'zondag',
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag',
] as const
const DUTCH_MONTHS = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
] as const

/** 'ma 3 feb' style label for a local ISO date. */
export function formatShortDutchDay(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((part) => Number(part))
  const date = new Date(y, (m ?? 1) - 1, d ?? 1)
  const weekday = DUTCH_WEEKDAYS_SHORT[(date.getDay() + 6) % 7]
  return `${weekday} ${d} ${DUTCH_MONTHS[(m ?? 1) - 1].slice(0, 3)}`
}

/** 'maandag 3 februari 2026' */
export function formatFullDutchDay(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((part) => Number(part))
  const date = new Date(y, (m ?? 1) - 1, d ?? 1)
  return `${DUTCH_WEEKDAYS_FULL[date.getDay()]} ${d} ${DUTCH_MONTHS[(m ?? 1) - 1]} ${y}`
}

/** Formats an ISO timestamp as short Dutch date+time, e.g. '3 feb, 14:05'. */
export function formatDutchDateTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp)
  if (Number.isNaN(date.getTime())) return ''
  const day = date.getDate()
  const month = DUTCH_MONTHS[date.getMonth()].slice(0, 3)
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day} ${month}, ${hours}:${minutes}`
}

export function isValidISODate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00`)
  return !Number.isNaN(date.getTime())
}
